import TemplateService from "./TemplateService";

const SIGNED_URL_UPLOAD_CONTENT_TYPE = "multipart/form-data";
const DEFAULT_ENTITY = "TEMPLATE";
const ASSET_TYPE_IMAGE = "IMAGE";

/**
 * Prefer permanent public URL. If BE echoed a GCS signed write URL, strip query.
 */
export const toPublicAssetUrl = (url) => {
  if (!url || typeof url !== "string") {
    return url;
  }

  try {
    const parsed = new URL(url);
    if (
      parsed.searchParams.has("X-Goog-Signature") ||
      parsed.searchParams.has("GoogleAccessId") ||
      parsed.searchParams.has("X-Goog-Algorithm")
    ) {
      return `${parsed.origin}${parsed.pathname}`;
    }
  } catch {
    // keep original
  }

  return url;
};

const CONTENT_TYPE_EXTENSION = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
};

const EXTENSION_CONTENT_TYPE = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
};

const getFileExtension = (filename) => {
  const extension = filename?.split(".").pop();

  if (!extension || extension === filename) {
    throw new Error(`File "${filename}" has no extension`);
  }

  return extension.toLowerCase();
};

const getUploadFileExtension = (file) => {
  const extensionFromMime = CONTENT_TYPE_EXTENSION[file.type?.toLowerCase()];

  if (extensionFromMime) {
    return extensionFromMime;
  }

  return getFileExtension(file.name);
};

const assertSupportedImageExtension = (fileExtension) => {
  if (!EXTENSION_CONTENT_TYPE[fileExtension]) {
    throw new Error(`File extension ".${fileExtension}" is not supported.`);
  }
};

const buildOriginalFilename = (file, fileExtension) => {
  if (file.name.toLowerCase().endsWith(`.${fileExtension}`)) {
    return file.name;
  }

  const baseName = file.name.includes(".") ? file.name.replace(/\.[^.]+$/, "") : file.name;

  return `${baseName || "file"}.${fileExtension}`;
};

const uploadToSignedUrl = async (signedUrl, file) => {
  const uploadResponse = await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": SIGNED_URL_UPLOAD_CONTENT_TYPE,
    },
    credentials: "omit",
  });

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload file to storage: ${uploadResponse.statusText}`);
  }
};

/**
 * Upload an image via signed URL flow (same contract as cohora-admin-react uploadSignedAsset).
 * GET /asset/v1/signedURL → PUT GCS → POST /asset/v1/signedURL/{entity}/asset
 */
export const uploadSignedAsset = async ({
  file,
  entity = DEFAULT_ENTITY,
  description = "",
}) => {
  if (!file) {
    throw new Error("File is required");
  }

  const fileExtension = getUploadFileExtension(file);
  assertSupportedImageExtension(fileExtension);

  const { data: signedUrlResponse } = await TemplateService.getPresignedUrl({
    entity,
    fileExtension,
  });

  const signedUrl =
    typeof signedUrlResponse === "string" ? signedUrlResponse : signedUrlResponse?.signedUrl;

  if (!signedUrl?.startsWith("https://")) {
    throw new Error("Invalid signed URL received from API");
  }

  await uploadToSignedUrl(signedUrl, file);

  const { data: asset } = await TemplateService.getAssetData(
    {
      url: signedUrl,
      originalFileAndExtension: buildOriginalFilename(file, fileExtension),
      type: ASSET_TYPE_IMAGE,
      description,
    },
    entity
  );

  return asset;
};

export default uploadSignedAsset;
